/**
 * Spaltenmaße der Kanban-Linse des Phase Boards (Issue #50), rein und testbar.
 *
 * Warum als Funktion statt als Klasse im JSX: die untere Grenze ist ein
 * Akzeptanzkriterium, kein Geschmack. Bei 1280×800 CSS-Pixeln — dem Zielschirm
 * des Nutzers — passen sechs Spalten NICHT nebeneinander; die Sicht lebt dort
 * davon, dass die Spalten auf KANBAN_COLUMN_MIN_PX stehen bleiben und der
 * Container horizontal scrollt. Wer die 210px senkt oder das minmax() gegen ein
 * `1fr` tauscht, zerlegt genau diesen Fall — deshalb steht die Regel an einer
 * Stelle und ist mit einem Test festgenagelt.
 *
 * Der obere Deckel verhindert, dass Spalten auf sehr breiten Schirmen zerlaufen.
 * Er wirkt erst, wenn der Container mehr Platz hat als n × MAX + Lücken; bei
 * sechs Phasen und --canvas-wide (1920px) ist die Containerbreite die bindende
 * Grenze, bei vier oder fünf Phasen dieser Deckel.
 */

/** Untere Grenze je Spalte. Darunter wird eine Karte unlesbar. */
export const KANBAN_COLUMN_MIN_PX = 210;

/** Obere Grenze je Spalte (Issue #50: von 320px angehoben). */
export const KANBAN_COLUMN_MAX_PX = 420;

/**
 * `grid-template-columns` der Kanban-Linse für `phaseCount` sichtbare Phasen.
 * Ohne sichtbare Phase gibt es keine Spalte — `repeat(0, …)` wäre ungültiges
 * CSS, deshalb `none`.
 */
export function kanbanColumnTemplate(phaseCount: number): string {
  if (phaseCount <= 0) return 'none';
  return `repeat(${phaseCount}, minmax(${KANBAN_COLUMN_MIN_PX}px, ${KANBAN_COLUMN_MAX_PX}px))`;
}

/**
 * Untergrenze der Projektspalte in der Matrix-Linse (Issue #50, Review-Nachtrag).
 * Muss bei 280px bleiben — das ist der bisherige feste Wert, unter den die
 * Spalte auf keinem Schirm fallen darf.
 */
export const MATRIX_PROJECT_COLUMN_MIN_PX = 280;

/**
 * Obergrenze der Projektspalte in der Matrix-Linse. Bei 1280×800 CSS-Pixeln
 * (Zielschirm) bleibt nach Sidebar (256px) und Innenabständen (main px-6 +
 * Canvas px-2, zusammen 64px) rund 960px Grid-Breite übrig. Die Projektspalte
 * wächst dort im Tracksizing-Schritt zuerst auf ihr Maximum (360px), bevor der
 * Rest an die sechs `1fr`-Phasenspalten geht — die behalten mit ~80px pro
 * Spalte genug Platz für die Status-Icons. Bei --canvas-wide (1920px) bleiben
 * es ~257px je Phasenspalte. 360px ist damit auf beiden Bildschirmen sicher,
 * ohne die Untergrenze zu berühren.
 */
export const MATRIX_PROJECT_COLUMN_MAX_PX = 360;

/**
 * `grid-template-columns` der Matrix-Linse für `phaseCount` sichtbare Phasen:
 * eine mitwachsende Projektspalte (280–360px) plus je eine `1fr`-Spalte pro
 * Phase. Ohne sichtbare Phase gibt es — wie bei `kanbanColumnTemplate` — keine
 * Spalte, `repeat(0, …)` wäre ungültiges CSS, deshalb `none`.
 */
export function matrixColumnTemplate(phaseCount: number): string {
  if (phaseCount <= 0) return 'none';
  return `minmax(${MATRIX_PROJECT_COLUMN_MIN_PX}px, ${MATRIX_PROJECT_COLUMN_MAX_PX}px) repeat(${phaseCount}, 1fr)`;
}

/**
 * Die PIPELINE-Linse steht bewusst nicht hier — sie brauchte für Issue #50 keinen
 * Code. Ihr Raster ist bereits `repeat(n, minmax(0, 1fr))` (`phase-board.tsx`) und
 * wächst damit automatisch mit dem breiteren Container mit; ein Deckel wie bei
 * Kanban wäre dort falsch, weil die Pipeline keine Kartenspalten, sondern
 * gleichverteilte Phasenbahnen zeigt. Das steht hier, damit der nächste Leser
 * „geprüft, kein Code nötig" von „vergessen" unterscheiden kann — das
 * Akzeptanzkriterium von #50 nennt alle drei Linsen ausdrücklich.
 */
